/*
 * Filename: \src\Common\FullTrailerContext.js
 * Created Date: 2025-11-27 13:30
 * Author: jdkohl
 *
 * Copyright (c) 2025 Logical Developments
 */

import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import useRest from '../Session/useRest';
import { allowFullTrailer } from '../Configuration/Config';

const FullTrailerContext = createContext(null);

/**
 * FullTrailerProvider - Provides Full Trailer state management across workflows
 * Manages API calls to check Full Trailer availability and maintains toggle state
 */
export function FullTrailerProvider({ children }) {
  const [isFullTrailer, setIsFullTrailer] = useState(false);
  const [fullTrailerRates, setFullTrailerRates] = useState([]);
  const [lastQuery, setLastQuery] = useState(null);
  const [error, setError] = useState(null);

  // API hook for checking Full Trailer availability
  const [trailerRequest, trailerResponse, trailerError, trailerLoading] = useRest('GET', 'options/ppi/trailer');

  // Process API response (only if feature enabled)
  useEffect(() => {
    if (!allowFullTrailer) return; // Early exit if disabled

    if (trailerError) {
      setError(trailerError);
      setFullTrailerRates([]);
    } else if (trailerResponse) {
      setFullTrailerRates(Array.isArray(trailerResponse) ? trailerResponse : []);
      setError(null);
    }
  }, [trailerResponse, trailerError]);

  // Derive availability from rates
  const isAvailable = useMemo(() =>
    allowFullTrailer ? fullTrailerRates.length > 0 : false,
    [fullTrailerRates]
  );

  /**
   * Check if the query parameters have changed to prevent redundant API calls
   */
  const shouldQuery = useCallback((sender, receiver) => {
    if (!allowFullTrailer) return false; // Disabled feature
    if (!sender?.suburb || !receiver?.suburb) return false;
    const newQuery = `${sender.suburb}-${sender.postCode}-${receiver.suburb}-${receiver.postCode}`;
    return newQuery !== lastQuery;
  }, [lastQuery]);

  /**
   * Reset all Full Trailer state
   */
  const reset = useCallback(() => {
    if (!allowFullTrailer) return; // No-op if disabled
    setIsFullTrailer(false);
    setFullTrailerRates([]);
    setError(null);
    setLastQuery(null);
  }, []);

  /**
   * Check Full Trailer availability for given sender/receiver pair
   */
  const checkAvailability = useCallback((senderData, receiverData) => {
    if (!allowFullTrailer) return; // No-op if disabled

    // Validate input data
    if (!senderData?.suburb || !receiverData?.suburb) {
      reset();
      return;
    }

    // Don't query if parameters haven't changed
    if (!shouldQuery(senderData, receiverData)) {
      return;
    }

    // Make API call
    trailerRequest({
      senderSuburb: senderData.suburb,
      senderState: senderData.state,
      senderPostCode: senderData.postCode,
      receiverSuburb: receiverData.suburb,
      receiverState: receiverData.state,
      receiverPostCode: receiverData.postCode
    });

    // Update last query to prevent duplicates
    setLastQuery(`${senderData.suburb}-${senderData.postCode}-${receiverData.suburb}-${receiverData.postCode}`);
  }, [shouldQuery, trailerRequest, reset]);

  /**
   * Toggle Full Trailer mode on/off
   */
  const setFullTrailer = useCallback((value) => {
    if (!allowFullTrailer) return; // No-op if disabled
    setIsFullTrailer(value);
  }, []);

  /**
   * Load Full Trailer state (for session restoration)
   */
  const loadState = useCallback((savedState) => {
    if (!allowFullTrailer) return; // No-op if disabled
    if (typeof savedState === 'boolean') {
      setIsFullTrailer(savedState);
    }
  }, []);

  const value = {
    isEnabled: allowFullTrailer,
    isFullTrailer: allowFullTrailer ? isFullTrailer : false,
    fullTrailerRates: allowFullTrailer ? fullTrailerRates : [],
    isAvailable: allowFullTrailer ? isAvailable : false,
    isLoading: allowFullTrailer ? trailerLoading : false,
    error: allowFullTrailer ? error : null,
    checkAvailability,
    setFullTrailer,
    reset,
    loadState
  };

  return (
    <FullTrailerContext.Provider value={value}>
      {children}
    </FullTrailerContext.Provider>
  );
}

/**
 * useFullTrailer - Hook to access Full Trailer context
 * @returns {Object} Full Trailer context value
 * @throws {Error} If used outside of FullTrailerProvider
 */
export function useFullTrailer() {
  const context = useContext(FullTrailerContext);
  if (!context) {
    throw new Error('useFullTrailer must be used within a FullTrailerProvider');
  }
  return context;
}
